import { Injectable, inject } from '@angular/core';
import { ConnectionService } from './connection.service';
import { environment } from '../../environments/environment';

export interface ThemePreset {
  id: string;
  nameKey: string;
  descKey: string;
  isPro: boolean;
  swatch: { cream: string; gold: string; rose: string; textDark: string };
}

const STORAGE_KEY = 'theme';
const DEFAULT_THEME = 'default';

/**
 * Rotte pubbliche mostrate a chi non ha effettuato login (es. link di firma
 * inviato allo sposo/a): restano sempre sul tema default, senza ereditare un
 * residuo lasciato in localStorage da un'altra sessione sullo stesso browser.
 */
const NO_THEME_ROUTES = ['/firma', '/f/', '/preventivo', '/p/', '/landing'];

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private connection = inject(ConnectionService);

  readonly presets: ThemePreset[] = [
    {
      id: 'default',
      nameKey: 'profile.theme.default_name',
      descKey: 'profile.theme.default_desc',
      isPro: false,
      swatch: { cream: '#f3f7f0', gold: '#fd5c30', rose: '#c482fd', textDark: '#19323c' },
    },
    {
      id: 'floreale',
      nameKey: 'profile.theme.floreale_name',
      descKey: 'profile.theme.floreale_desc',
      isPro: true,
      swatch: { cream: '#fdf6f0', gold: '#d4956a', rose: '#c47a8a', textDark: '#2e1f1f' },
    },
    {
      id: 'caldo',
      nameKey: 'profile.theme.caldo_name',
      descKey: 'profile.theme.caldo_desc',
      isPro: true,
      swatch: { cream: '#fbf4ee', gold: '#e8703f', rose: '#b98ee8', textDark: '#2a2420' },
    },
    {
      id: 'lusso',
      nameKey: 'profile.theme.lusso_name',
      descKey: 'profile.theme.lusso_desc',
      isPro: true,
      swatch: { cream: '#f7f6f4', gold: '#c24a26', rose: '#8b5fc4', textDark: '#14181b' },
    },
    {
      id: 'botanico',
      nameKey: 'profile.theme.botanico_name',
      descKey: 'profile.theme.botanico_desc',
      isPro: true,
      swatch: { cream: '#faf8fc', gold: '#ff7a4d', rose: '#d9a6ff', textDark: '#241e2e' },
    },
    {
      id: 'scura',
      nameKey: 'profile.theme.scura_name',
      descKey: 'profile.theme.scura_desc',
      isPro: true,
      swatch: { cream: '#f1f3f5', gold: '#e8623a', rose: '#8f6fc9', textDark: '#19232e' },
    },
    {
      id: 'mediterraneo',
      nameKey: 'profile.theme.mediterraneo_name',
      descKey: 'profile.theme.mediterraneo_desc',
      isPro: true,
      swatch: { cream: '#f5ede0', gold: '#d8552b', rose: '#9a6fd1', textDark: '#2b211a' },
    },
    {
      id: 'notte',
      nameKey: 'profile.theme.notte_name',
      descKey: 'profile.theme.notte_desc',
      isPro: true,
      swatch: { cream: '#16141c', gold: '#ff8257', rose: '#d9a0ff', textDark: '#f2eef7' },
    },
    {
      id: 'alto-contrasto',
      nameKey: 'profile.theme.alto_contrasto_name',
      descKey: 'profile.theme.alto_contrasto_desc',
      isPro: false,
      swatch: { cream: '#ffffff', gold: '#b3390f', rose: '#6a2fa6', textDark: '#000000' },
    },
  ];

  private current = DEFAULT_THEME;

  constructor() {
    const path = window.location.pathname;
    this.applyTheme(this.isNoThemeRoute(path) ? DEFAULT_THEME : this.savedOrDefault());
  }

  private isNoThemeRoute(path: string): boolean {
    return environment.landingOnly || NO_THEME_ROUTES.some((p) => path.startsWith(p));
  }

  private savedOrDefault(): string {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && this.presets.some((p) => p.id === saved) ? saved : DEFAULT_THEME;
  }

  /** Da chiamare ad ogni cambio rotta (router events): forza il default sulle
   * rotte pubbliche, altrimenti ripristina il tema salvato dall'utente. */
  syncWithRoute(path: string): void {
    const wanted = this.isNoThemeRoute(path) ? DEFAULT_THEME : this.savedOrDefault();
    if (wanted !== this.current) this.applyTheme(wanted);
  }

  get activeTheme(): string {
    return this.current;
  }

  /** Applica solo lato client (classe su <html>), senza persistere né chiamare il backend. */
  applyTheme(id: string): void {
    document.documentElement.classList.remove(
      ...this.presets.map((p) => `theme-${p.id}`),
    );
    document.documentElement.classList.add(`theme-${id}`);
    this.current = id;
  }

  /** Applica, persiste in locale e salva sul profilo utente. */
  setTheme(id: string): void {
    this.applyTheme(id);
    localStorage.setItem(STORAGE_KEY, id);
    this.connection.put('@auth/preferences', { theme: id }, () => {}, () => {});
  }

  /** Allinea il tema locale a quello salvato sul backend (es. dopo login su un altro device). */
  reconcileFromProfile(theme: string | null | undefined): void {
    if (!theme || theme === this.current) return;
    if (!this.presets.some((p) => p.id === theme)) return;
    this.applyTheme(theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }
}
